import type { CSSProperties } from "react";
import {
  type BackgroundContrast,
  INK_HEX,
  PAPER_HEX,
  SHEEN_ALPHA,
} from "@/lib/dashboard-home/contrast";

/**
 * Habillage des surfaces translucides de l'accueil (carte titre, pastille de
 * l'instance, Raccourcis, Mes tutoriels, Configurer) selon le fond mesuré.
 * Les trois cartes de navigation restent opaques quel que soit le fond.
 */
export type GlassTheme = {
  /** Couleur du texte et des icônes posés sur le verre. */
  text: string;
  surface: CSSProperties;
  /** Fond des pastilles carrées d'icône. */
  iconBox: CSSProperties;
};

function rgba(hex: string, alpha: number): string {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

function glassFill({ tone, scrim }: BackgroundContrast): string {
  if (tone === "light") return rgba(PAPER_HEX, scrim);
  // Teinte sombre sans voile nécessaire : verre blanc léger de la maquette.
  return scrim === 0 ? rgba(PAPER_HEX, SHEEN_ALPHA) : rgba(INK_HEX, scrim);
}

export function glassTheme(contrast: BackgroundContrast): GlassTheme {
  const dark = contrast.tone === "dark";
  const text = dark ? PAPER_HEX : INK_HEX;
  return {
    text,
    surface: {
      backgroundColor: glassFill(contrast),
      borderColor: dark ? rgba(PAPER_HEX, 0.5) : "#ffffff",
      color: text,
    },
    iconBox: {
      backgroundColor: dark ? "rgba(0, 0, 0, 0.24)" : "rgba(0, 0, 0, 0.08)",
    },
  };
}

/** Blocs (Raccourcis, Tutoriels, Configurer). Le flou fait partie du calcul de contraste. */
export const GLASS_BLOCK_CLASS =
  "rounded-xl border border-solid backdrop-blur-[50px] backdrop-saturate-150 shadow-[0_8px_32px_rgba(0,0,0,0.12),inset_0_1px_0_rgba(255,255,255,0.25)]";

/** Petites surfaces (carte titre, pastille de l'instance), au flou de la maquette. */
export const GLASS_CHIP_CLASS =
  "border-[0.5px] border-solid backdrop-blur-[35px] backdrop-saturate-150 shadow-[inset_0_1px_0_rgba(255,255,255,0.25)]";
